/**
 * About Cyannect
 * Long-form marketing page with copy provided by the founder.
 * Uses existing `pf-card` / `pf-btn-primary` / `pf-chip` design system.
 */
import { Link } from "react-router-dom";
import PageBackground from "@/components/PageBackground";
import {
  Sparkles, ArrowRight, ShoppingBag, Store, Handshake, Users, Compass,
  Zap, LineChart, MapPin, ShieldCheck, Smartphone, LayoutDashboard,
  Radar, CreditCard, Building2, Rocket, Check,
  Quote, MousePointerClick, DollarSign, TrendingUp,
} from "lucide-react";

/**
 * Mobile app link config. When the app is live, replace `null` with the
 * production listing URLs. UI automatically flips from "Coming soon" state
 * to real download buttons — no other code changes required.
 */
const MOBILE_APP_LINKS = {
  ios: null,       // e.g. "https://apps.apple.com/app/cyannect/id123456789"
  android: null,   // e.g. "https://play.google.com/store/apps/details?id=com.cyannect"
};

const TRUSTED_BY = [
  "NORTHWIND",
  "MERIDIAN",
  "ATLAS & CO",
  "PALOMA",
  "HELIX LABS",
  "BRIGHTLEAF",
];

const TESTIMONIALS = [
  {
    quote: "Cyannect gave us the missing puzzle piece — we can finally see which promotions actually drive purchases, not just clicks. Our fashion campaigns saw a 3.4× lift in verified conversions in the first quarter alone.",
    name: "Elena Marchetti",
    title: "Head of Growth, Meridian Apparel",
    stat: "3.4× conversion lift",
  },
  {
    quote: "The reseller network + attribution engine is essentially a done-for-you affiliate program. We onboarded twelve partners in a week and stopped paying for phantom traffic. The ledger is transparent enough that finance stopped asking us to reconcile.",
    name: "Marcus Kaine",
    title: "VP Marketing, Northwind Retail",
    stat: "12 partners onboarded in 7 days",
  },
  {
    quote: "We used to run coupon campaigns and hope. Cyannect measures every dollar back to the customer who acted on it. Our per-customer acquisition cost dropped by 41% because we stopped guessing which channel worked.",
    name: "Priya Ravindran",
    title: "Founder, Paloma Home",
    stat: "-41% CAC",
  },
];

const merchantUses = [
  "Create and manage coupons and promotional offers",
  "Target customers by location",
  "Distribute offers through Cyannect",
  "Work with approved resellers and distribution partners",
  "Track visitors generated by their offers",
  "Measure conversions and attributed transactions",
  "Understand campaign performance",
  "Manage billing and performance-based campaigns",
  "Integrate Cyannect with their website and checkout",
];

const consumerUses = [
  "Discover offers and discounts",
  "Find offers relevant to their location",
  "Search for brands and merchants",
  "Explore local and online deals",
  "Activate available coupons",
  "Click through to participating merchant websites",
  "Shop directly with the merchant",
  "Keep track of saved and previously activated offers",
];

const journeySteps = [
  { n: "1", title: "Download the Cyannect App", body: "Available for iOS and Android." },
  { n: "2", title: "Create an account", body: "Consumers create a free Cyannect account and set their preferences." },
  { n: "3", title: "Choose their location", body: "With the appropriate permission, the app uses device location to help identify relevant offers. Cyannect can also use other location signals, such as approximate IP-based geography, where appropriate." },
  { n: "4", title: "Discover offers", body: "Browse by nearby businesses, categories, brands, online stores, local offers or personalized recommendations." },
  { n: "5", title: "Activate an offer", body: "The consumer selects an offer they are interested in." },
  { n: "6", title: "Visit the merchant", body: "Cyannect directs the consumer to the participating merchant's website or provides the applicable redemption instructions." },
  { n: "7", title: "Shop with the merchant", body: "The consumer completes their purchase directly with the merchant." },
];

const businessTypes = [
  "E-commerce brand", "Retailer", "Direct-to-consumer business", "Restaurant",
  "Local business", "Consumer brand", "Regional merchant", "Online service provider",
];

const merchantGoals = [
  "Promote new products", "Increase website traffic", "Attract new customers",
  "Promote seasonal campaigns", "Target specific geographic markets",
  "Increase coupon usage", "Measure campaign performance", "Track attributed conversions",
];

const brandBenefits = [
  "Consumer discovery", "Geographic targeting", "Offer distribution",
  "Performance tracking", "Conversion attribution", "Merchant analytics", "Integration capabilities",
];

const locationSignals = [
  "Device/browser location", "Approximate IP-based location", "User-selected location",
  "City", "Province or state", "Country", "Geographic targeting defined by the merchant",
];

const platformParts = [
  { icon: Smartphone, title: "Consumer Mobile App", body: "For discovering and activating offers." },
  { icon: LayoutDashboard, title: "Merchant Web Platform", body: "For managing businesses, coupons, campaigns and performance." },
  { icon: Handshake, title: "Reseller Platform", body: "For approved partners distributing merchant offers." },
  { icon: LineChart, title: "Developer Platform", body: "For merchants and developers integrating Cyannect into their websites and checkout systems." },
  { icon: Radar, title: "Attribution Engine", body: "For connecting Cyannect-generated traffic with eligible merchant conversions." },
  { icon: CreditCard, title: "Payment Integrations", body: "As the platform develops, Cyannect plans to support integrations with appropriate payment providers, subject to technical, commercial and regulatory requirements." },
];

const visionStack = [
  "Offer Discovery", "Customer Acquisition", "Conversion Attribution",
  "Merchant Analytics", "Payment Integrations", "Performance Commerce",
];

function Chip({ icon: Icon, children, tone = "cyan" }) {
  const map = {
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
    mint: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${map[tone]}`}>
      {Icon && <Icon className="w-3 h-3" />} {children}
    </span>
  );
}

export default function About() {
  return (
    <div className="relative min-h-screen" data-testid="about-page">
      <PageBackground />

      {/* HERO */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pt-20 sm:pt-28 pb-16 text-center" data-testid="about-hero">
        <Chip icon={Sparkles} tone="cyan">About Cyannect</Chip>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900">
          Connecting Businesses with Customers <br className="hidden md:block" />
          <span className="pf-gradient-text-2">Who Are Ready to Discover More.</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto mt-6 leading-relaxed">
          At Cyannect, we believe discounts should do more than attract attention — they should help businesses acquire real customers and measure the results. Cyannect is a performance-driven commerce platform that connects brands, merchants, resellers and consumers through relevant offers, discounts and promotional campaigns.
        </p>
        <p className="text-base text-slate-600 max-w-3xl mx-auto mt-4 leading-relaxed">
          Our platform gives businesses a simple way to publish and distribute offers, reach customers based on location and interests, and measure the traffic and transactions generated through their campaigns. For consumers, Cyannect provides a convenient mobile experience to discover offers that are relevant to where they are and what they are interested in.
        </p>
      </section>

      {/* HOW IT WORKS — two sides */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-16" data-testid="about-two-sides">
        <div className="text-center mb-10">
          <Chip icon={Compass} tone="mint">How Cyannect Works</Chip>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">
            Two sides of commerce, one <span className="pf-gradient-text-2">measured journey.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <article className="pf-card p-8" data-testid="about-side-business">
            <div className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4 bg-cyan-50 text-cyan-700">
              <Building2 className="w-6 h-6" />
            </div>
            <h3 className="font-display text-2xl font-medium tracking-tight text-slate-900">Businesses</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Brands and merchants use the Cyannect Business Platform to:
            </p>
            <ul className="mt-4 space-y-2">
              {merchantUses.map((u) => (
                <li key={u} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" /><span>{u}</span>
                </li>
              ))}
            </ul>
          </article>
          <article className="pf-card p-8" data-testid="about-side-consumer">
            <div className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4 bg-emerald-50 text-emerald-700">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <h3 className="font-display text-2xl font-medium tracking-tight text-slate-900">Consumers</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Consumers use the Cyannect mobile app to:
            </p>
            <ul className="mt-4 space-y-2">
              {consumerUses.map((u) => (
                <li key={u} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span>{u}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-6 italic border-t border-slate-100 pt-4">
              The consumer does not need to purchase through Cyannect to shop with a merchant. Cyannect can direct the customer to the merchant's own website and help the business measure the resulting customer journey.
            </p>
          </article>
        </div>
      </section>

      {/* CONSUMER JOURNEY */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="about-journey">
        <div className="text-center mb-10">
          <Chip icon={Smartphone} tone="cyan">The Cyannect Consumer App</Chip>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">
            Your Offers. Your Location. <span className="pf-gradient-text-2">Your Choices.</span>
          </h2>
          <p className="text-sm text-slate-600 max-w-2xl mx-auto mt-3 leading-relaxed">
            Instead of searching through hundreds of unrelated coupons, consumers can use the app to discover offers based on their location, interests and preferences.
          </p>
        </div>
        <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {journeySteps.map((step, i) => (
            <li key={step.n} className="pf-card p-5" data-testid={`about-journey-step-${i}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-400 text-white font-display text-sm flex items-center justify-center">{step.n}</div>
              <div className="mt-3 text-sm font-semibold text-slate-900">{step.title}</div>
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* WHY BUSINESSES CHOOSE */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="about-why-business">
        <div className="pf-card p-8 sm:p-12 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 ring-1 ring-cyan-100">
          <Chip icon={LineChart} tone="mint">Measurable customer acquisition</Chip>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">
            Turn Promotions Into <span className="pf-gradient-text-2">Measurable Customer Acquisition.</span>
          </h2>
          <p className="text-sm text-slate-600 mt-4 leading-relaxed max-w-3xl">
            Traditional promotions can tell a merchant how many people saw an advertisement — but businesses ultimately want to know: <em>Did the campaign bring us customers?</em> Cyannect is designed around this question.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2 font-display text-lg text-slate-900">
            <span className="rounded-full bg-white/70 border border-cyan-200 px-3 py-1.5 text-sm">Offer</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className="rounded-full bg-white/70 border border-cyan-200 px-3 py-1.5 text-sm">Click</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className="rounded-full bg-white/70 border border-emerald-200 px-3 py-1.5 text-sm">Merchant Visit</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className="rounded-full bg-white/70 border border-emerald-200 px-3 py-1.5 text-sm">Conversion</span>
          </div>
          <p className="text-sm text-slate-600 mt-6 leading-relaxed max-w-3xl">
            This gives participating businesses the ability to understand the performance of their Cyannect campaigns and make better decisions about where to invest their promotional budgets.
          </p>
        </div>
      </section>

      {/* BUILT FOR MODERN BRANDS */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="about-built-for">
        <div className="text-center mb-8">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900">
            Built for <span className="pf-gradient-text-2">Modern Brands and Merchants.</span>
          </h2>
          <p className="text-sm text-slate-600 max-w-2xl mx-auto mt-3 leading-relaxed">
            Cyannect is designed for businesses that already have their own online presence and checkout experience.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
          {businessTypes.map((t) => (
            <span key={t} className="rounded-full bg-white border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm">
              {t}
            </span>
          ))}
        </div>
        <p className="text-sm text-slate-600 text-center max-w-3xl mx-auto mt-8 leading-relaxed">
          Businesses can continue using their existing website and checkout infrastructure while integrating Cyannect's tracking and conversion capabilities.
        </p>
      </section>

      {/* THREE OBJECTIVES */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="about-objectives">
        <div className="text-center mb-10">
          <Chip icon={Sparkles} tone="violet">More Than a Coupon Platform</Chip>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">
            Performance Commerce Infrastructure, <span className="pf-gradient-text-2">not another directory.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { n: "1", title: "Discover", body: "Help consumers discover offers that are relevant to them.", tone: "cyan" },
            { n: "2", title: "Connect", body: "Connect interested consumers with participating merchants.", tone: "mint" },
            { n: "3", title: "Measure", body: "Give merchants visibility into the traffic, conversions and value generated by their offers.", tone: "violet" },
          ].map((o) => (
            <div key={o.n} className="pf-card p-6 text-center" data-testid={`about-objective-${o.n}`}>
              <div className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-display text-lg ${
                o.tone === "cyan" ? "bg-cyan-100 text-cyan-700" :
                o.tone === "mint" ? "bg-emerald-100 text-emerald-700" :
                "bg-violet-100 text-violet-700"
              }`}>{o.n}</div>
              <h3 className="font-display text-xl font-medium tracking-tight text-slate-900 mt-4">{o.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{o.body}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-600 text-center max-w-3xl mx-auto mt-8 leading-relaxed italic">
          This approach creates value for both sides of the marketplace. Consumers receive access to relevant offers. Merchants receive a measurable customer acquisition channel.
        </p>
      </section>

      {/* LOCATION */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16" data-testid="about-location">
        <div className="pf-card p-8 sm:p-12">
          <Chip icon={MapPin} tone="cyan">Our Approach to Location</Chip>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">Relevance matters.</h2>
          <p className="text-sm text-slate-600 mt-4 leading-relaxed">
            A consumer looking for an offer in Toronto may not be interested in an offer that is only available hundreds of kilometres away. Cyannect is therefore designed to use location signals to improve offer relevance.
          </p>
          <p className="text-sm text-slate-600 mt-4">Depending on availability and user permission, the platform may consider:</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {locationSignals.map((s) => (
              <span key={s} className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1 text-xs text-slate-700">{s}</span>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 border-t border-slate-100 pt-4">
            <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <p>Location information is used to help provide a more relevant experience and is handled in accordance with applicable privacy requirements and user permissions.</p>
          </div>
        </div>
      </section>

      {/* FOR MERCHANTS / BRANDS / RESELLERS */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="about-audiences">
        <div className="grid md:grid-cols-3 gap-4">
          <article className="pf-card p-6" data-testid="about-audience-merchants">
            <div className="inline-flex w-11 h-11 rounded-2xl items-center justify-center mb-3 bg-cyan-50 text-cyan-700">
              <Store className="w-5 h-5" />
            </div>
            <h3 className="font-display text-xl font-medium tracking-tight text-slate-900">For Merchants</h3>
            <p className="text-sm text-slate-600 mt-1">Put Your Offers in Front of Interested Customers.</p>
            <ul className="mt-4 space-y-1.5">
              {merchantGoals.map((g) => (
                <li key={g} className="flex items-start gap-2 text-xs text-slate-700">
                  <Check className="w-3 h-3 text-cyan-600 mt-1 flex-shrink-0" /><span>{g}</span>
                </li>
              ))}
            </ul>
          </article>
          <article className="pf-card p-6" data-testid="about-audience-brands">
            <div className="inline-flex w-11 h-11 rounded-2xl items-center justify-center mb-3 bg-emerald-50 text-emerald-700">
              <Rocket className="w-5 h-5" />
            </div>
            <h3 className="font-display text-xl font-medium tracking-tight text-slate-900">For Brands</h3>
            <p className="text-sm text-slate-600 mt-1">Expand Your Customer Reach.</p>
            <ul className="mt-4 space-y-1.5">
              {brandBenefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-xs text-slate-700">
                  <Check className="w-3 h-3 text-emerald-600 mt-1 flex-shrink-0" /><span>{b}</span>
                </li>
              ))}
            </ul>
          </article>
          <article className="pf-card p-6" data-testid="about-audience-resellers">
            <div className="inline-flex w-11 h-11 rounded-2xl items-center justify-center mb-3 bg-violet-50 text-violet-700">
              <Handshake className="w-5 h-5" />
            </div>
            <h3 className="font-display text-xl font-medium tracking-tight text-slate-900">For Resellers & Partners</h3>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Cyannect can also support approved resellers and distribution partners who help merchants reach additional customers.
            </p>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Resellers can access eligible offers, distribute them through approved channels and monitor the performance generated by their activities.
            </p>
          </article>
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="about-technology">
        <div className="text-center mb-10">
          <Chip icon={Zap} tone="cyan">Our Technology</Chip>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">
            A scalable platform for <span className="pf-gradient-text-2">every audience.</span>
          </h2>
          <p className="text-sm text-slate-600 max-w-2xl mx-auto mt-3 leading-relaxed">
            Cyannect is being designed as a scalable technology platform with separate experiences for consumers, merchants and partners.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformParts.map((p) => (
            <div key={p.title} className="pf-card p-5" data-testid={`about-tech-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-slate-50 border border-slate-200 text-slate-700 mb-3">
                <p.icon className="w-5 h-5" />
              </div>
              <h4 className="font-display text-base font-medium tracking-tight text-slate-900">{p.title}</h4>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VISION */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16 text-center" data-testid="about-vision">
        <Chip icon={Rocket} tone="mint">Our Long-Term Vision</Chip>
        <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4">
          A technology layer connecting <span className="pf-gradient-text-2">consumer demand and commerce.</span>
        </h2>
        <div className="mt-8 space-y-2">
          {visionStack.map((v, i) => (
            <div key={v}>
              <div className="pf-card inline-block px-6 py-3 text-sm font-semibold text-slate-800 min-w-[280px]" data-testid={`about-vision-step-${i}`}>
                {v}
              </div>
              {i < visionStack.length - 1 && (
                <div className="text-slate-300 text-xl leading-none py-1">↓</div>
              )}
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-600 mt-8 max-w-2xl mx-auto leading-relaxed italic">
          We believe the future of promotions is not simply about distributing more coupons. It is about connecting the right offer with the right customer and helping businesses understand the value that interaction creates.
        </p>
      </section>

      {/* COMMITMENT */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16" data-testid="about-commitment">
        <div className="pf-card p-8 sm:p-12 bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-cyan-300">
              <Sparkles className="w-3 h-3" /> Our Commitment
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mt-4">
              Better offers for consumers. <br />
              Better customers for merchants. <br />
              <span className="pf-gradient-text-2">Better measurement for everyone.</span>
            </h2>
            <p className="text-sm text-slate-300 mt-6 leading-relaxed max-w-2xl">
              We want Cyannect to make promotional commerce more relevant, measurable and transparent. Whether you are a brand looking to acquire customers, a merchant looking to increase sales, a reseller looking for new offers to distribute, or a consumer looking for a better deal — Cyannect is being built to connect you.
            </p>
          </div>
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-24" data-testid="about-join">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900">
            Join the <span className="pf-gradient-text-2">Cyannect Network.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="pf-card p-6 text-center" data-testid="about-join-business">
            <Building2 className="w-8 h-8 text-cyan-600 mx-auto" />
            <h3 className="font-display text-lg font-medium tracking-tight text-slate-900 mt-3">Are you a brand or merchant?</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">Create your business profile and start exploring how Cyannect can help you reach customers and measure your campaigns.</p>
            <Link to="/register?role=merchant" className="pf-btn-primary mt-4 px-5 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" data-testid="about-cta-merchant">
              Join as a Business <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="pf-card p-6 text-center" data-testid="about-join-consumer">
            <Smartphone className="w-8 h-8 text-emerald-600 mx-auto" />
            <h3 className="font-display text-lg font-medium tracking-tight text-slate-900 mt-3">Are you a consumer?</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">Save on every brand you love — earn cashback, unlock coupons, and swap rewards for gift cards, all from your browser.</p>
            <Link to="/shoppers" className="pf-btn-primary mt-4 px-5 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" data-testid="about-cta-shopper">
              Learn more <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="pf-card p-6 text-center" data-testid="about-join-reseller">
            <Handshake className="w-8 h-8 text-violet-600 mx-auto" />
            <h3 className="font-display text-lg font-medium tracking-tight text-slate-900 mt-3">Are you a reseller?</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">Join the Cyannect partner network and explore available merchant offers.</p>
            <Link to="/reseller" className="pf-btn-glass mt-4 px-5 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" data-testid="about-cta-reseller">
              Become a Partner <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="text-center mt-16">
          <div className="font-display text-2xl tracking-tight text-slate-900">CYANNECT</div>
          <p className="text-xs uppercase tracking-[0.4em] text-slate-500 mt-2">Discover · Connect · Save</p>
          <a href="https://cyannect.com" className="text-xs text-cyan-700 hover:text-cyan-900 mt-2 inline-block font-mono">cyannect.com</a>
        </div>
      </section>
    </div>
  );
}
